/* eslint-disable import/no-cycle */
import ObjectUtil from '@leverege/object-util';
import Obj from './Obj.js';
import Utils from '../Utils.js';
import Access from './InterfaceReducer.js';
import Constants from '../Constants.js';
import InterfaceActions from './InterfaceActions.js';
import Can from './Can.js';
const {
  getId
} = Utils;

/**
 * Class representing the interface route of the api
 */
class Interface {
  /**
   * create the Interface
   * @param {string} system id or alias of the system to connect to
   * @param {string} type the alias of the type of blueprint/devices
   */
  constructor(system, type) {
    this.systemId = getId(system);
    this.type = type;
    this.path = `${this.systemId}/${type}`;
  }
  can() {
    return Can.create(this.path, true, true);
  }

  /**
   * api call to get the list of devices of the provided type
   * @param {object} options to be included in the api call
   * @returns {function} redux thunk function
   */
  list(options, queryName = null) {
    /**
     * @returns {promise} represents the result of the api call
     */
    return InterfaceActions.list(this.path, null, Constants.INTERFACE_LIST, options, queryName);
  }

  /**
   * locally filters the results
   * @param {object} filter the filter object for the path
   */
  filter(filter) {
    return {
      type: Constants.INTERFACE_FILTER,
      filter,
      path: this.path
    };
  }

  /**
   * api call to create a device of the provided type
   * @param {object} obj search to execute
   * @param {object} options to be included in the api call
   * @returns {function} redux thunk function
   */
  search(obj, options, queryName = null) {
    /**
     * @returns {promise} represents the result of the api call
     */
    return InterfaceActions.search(this.path, obj, null, Constants.INTERFACE_LIST, options, queryName);
  }

  /**
   * api call to create a device of the provided type
   * @param {object} obj device to be created
   * @param {object} options to be included in the api call
   * @returns {function} redux thunk function
   */
  create(obj, options) {
    /**
     * @returns {promise} represents the result of the api call
     */
    return InterfaceActions.create(this.path, obj, null, Constants.INTERFACE_CREATE_ADD, options);
  }

  /**
   * api call to delete a device of the provided type
   * @param {string} id id of the device to be deleted
   * @param {object} options to be included in the api call
   * @returns {function} redux thunk function
   */
  delete(id, options) {
    /**
     * @returns {promise} represents the result of the api call
     */
    return InterfaceActions.remove(this.path, id, options);
  }

  /**
   * api call to update a child of the current path
   * @param {string} id id of the item to be updated
   * @param {object} data values of the item to be changed
   * @param {object} options api options
   * @returns {function} redux thunk function
   */
  update(id, data, options) {
    /**
     * @returns {Promise} represents the result of the api call to update the item
     */
    return this.obj(id).update(data, options);
  }

  /**
   * Returns a action to perform a getDeviceByNetworkAlias
   */
  getDeviceByNetworkAlias(network, key, value, options) {
    return InterfaceActions.getDeviceByNetworkAlias(this.path, network, key, value, options);
  }

  /**
   * makes an api call to get the history of an object at the path
   * @param {object} query.query query params that the server will use
   * @param {string} query.queryName query identifier
   * @param {object} options api options
   * @returns {function} redux thunk function
   */
  history(query, options) {
    return InterfaceActions.getHistory(`${this.path}.history`, this.parentPath, {
      ...options,
      q: JSON.stringify(query.query)
    }, query.queryName);
  }
  events(query, options) {
    return InterfaceActions.getHistory(`${this.path}.events`, this.parentPath, {
      ...options,
      q: JSON.stringify(query.query)
    }, query.queryName);
  }

  /**
   * api call to get the model of a device of the provided type
   * @param {object} options to be included in the api call
   * @returns {function} redux thunk function
   */
  model(options) {
    /**
     * @returns {promise} represents the result of the api call
     */
    return InterfaceActions.get(`${this.path}.model`, this.isInGroup ? this.parentPath : null, options);
  }

  /**
   * returns an instance of the obj class with the current path prepended
   * @param {string} id
   * @return {Obj} has the current path with the id
   */
  obj(id) {
    return Obj.create(this.path, id);
  }

  /**
   * @param {object} state the current redux state
   * @param {object} opts
   * @returns {object} results, in the form of a list
   */
  getList(state, opts, queryName) {
    return Access.getList(state, this.path, opts, queryName);
  }

  /**
   * gets the results of a specific query
   * @param {object} state the current redux state
   * @param {string} queryName identifier of the query
   * @returns {object} the results of the query
   */
  getHistory(state, queryName, opts) {
    return Access.getHistory(state, `${this.path}.history`, queryName, opts);
  }
  getEvents(state, queryName, opts) {
    return Access.getHistory(state, `${this.path}.events`, queryName, opts);
  }

  /**
   * @param {object} state the current redux state
   * @returns {object} returns a model for the query
   */
  getModel(state, queryName) {
    return Access.getObject(state, `${this.path}.model`, queryName);
  }

  /**
   * @param {object} state the current redux state
   * @returns {boolean} whether the query is loading
   */
  isLoading(state, queryName) {
    return Access.isLoading(state, this.path, queryName);
  }

  /**
   * @param {object} state the current redux state
   * @returns {boolean} whether the query had an error
   */
  isError(state, queryName) {
    return Access.isError(state, this.path, queryName);
  }

  /**
   * @param {object} state the current redux state
   * @returns {boolean} whether the query is finished
   */
  isDone(state, queryName) {
    return Access.isDone(state, this.path, queryName);
  }

  /**
   * @param {object} state the current redux state
   * @returns {boolean} whether or not the results can be returned as a table
   */
  isTable(state, queryName) {
    return Access.isTable(state, this.path, queryName);
  }

  /**
   * @param {object} state the current redux state
   * @returns {Error} an error if one exists, otherwise undefined
   */
  getError(state, queryName) {
    return Access.getError(state, this.path, queryName);
  }

  /**
   * @param {object} state the current redux state
   * @returns {boolean} whether the query is loading
   */
  isHistoryLoading(state, queryName) {
    return Access.isLoading(state, `${this.path}.history`, queryName);
  }

  /**
   * @param {object} state the current redux state
   * @returns {boolean} whether the query had an error
   */
  isHistoryError(state, queryName) {
    return Access.isError(state, `${this.path}.history`, queryName);
  }

  /**
   * @param {object} state the current redux state
   * @returns {boolean} whether the query is finished
   */
  isHistoryDone(state, queryName) {
    return Access.isDone(state, `${this.path}.history`, queryName);
  }

  /**
   * @param {object} state the current redux state
   * @returns {Error} an error if one exists, otherwise undefined
   */
  getHistoryError(state, queryName) {
    return Access.getError(state, `${this.path}.history`, queryName);
  }

  /**
   * @param {object} state the current redux state
   * @returns {boolean} whether the query is loading
   */
  isEventsLoading(state, queryName) {
    return Access.isLoading(state, `${this.path}.events`, queryName);
  }

  /**
   * @param {object} state the current redux state
   * @returns {boolean} whether the query had an error
   */
  isEventsError(state, queryName) {
    return Access.isError(state, `${this.path}.events`, queryName);
  }

  /**
   * @param {object} state the current redux state
   * @returns {boolean} whether the query is finished
   */
  isEventsDone(state, queryName) {
    return Access.isDone(state, `${this.path}.events`, queryName);
  }

  /**
   * @param {object} state the current redux state
   * @returns {Error} an error if one exists, otherwise undefined
   */
  getEventsError(state, queryName) {
    return Access.getError(state, `${this.path}.events`, queryName);
  }
}
const TEXT_FILTER = (filter, items) => {
  return items.filter(item => {
    const flat = ObjectUtil.flatten(item);
    return Object.values(flat).some(key => typeof key === 'string' && key.toLowerCase().includes(filter.text.toLowerCase()));
  });
};

// Lookup for Filters
const FILTERS = {
  text: TEXT_FILTER
};
const create = (system, type) => {
  return new Interface(system, type);
};

/**
 * Registers a filter function against the filter type.
 * @param {string} name the type name of the filter
 * @param {function} filter the function is given ( filter, items ) and should return
 * a new list, filterer as needed. The supplied filter and list should NOT be modified.
 */
const setFilter = (name, filter) => {
  FILTERS[name] = filter;
};

/**
 * Returns the filter function registerd at the name
 */
const getFilter = name => {
  return FILTERS[name];
};

/**
 * Returns the default text filter
 */
const getTextFilter = () => {
  return TEXT_FILTER;
};
export default {
  create,
  setFilter,
  getFilter,
  getTextFilter,
  setCacheTime: InterfaceActions.setCacheTime,
  getCacheTime: InterfaceActions.getCacheTime
};